import { atom, useRecoilState, useResetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "../../Functions";
import { EssayTopic, getMrMateTopics } from "../../Data/Social/MrMate";
import { socialDatabase } from "../../Data/Social/Database";

export type MrMateState = {
    topics: EssayTopic[];
    loading: boolean;
    error: string | null;
    lastUpdated: number | null;
};

export type UseMrMateReturn = {
    topics: EssayTopic[];
    loading: boolean;
    error: string | null;
    lastUpdated: number | null;
    
    // Functions
    fetchTopics: () => Promise<EssayTopic[]>;
    refreshTopics: () => Promise<EssayTopic[]>;
    
    reset: () => void;
};

// Mr. MATE state atom - holds recent 3 EssayTopics
export const mrMateState = atom<MrMateState>({
    key: "mrMateState" + randomString(4),
    default: {
        topics: [],
        loading: false,
        error: null,
        lastUpdated: null
    },
    effects: [
        ({ setSelf }) => {
            const loadData = async () => {
                try {
                    await socialDatabase.init();
                    
                    // Load cached essay topics
                    const cachedTopics = await socialDatabase.getEssayTopics();
                    
                    if (cachedTopics.length > 0) {
                        setSelf({ 
                            topics: cachedTopics,
                            loading: false, 
                            error: null,
                            lastUpdated: Math.max(...cachedTopics.map(t => t.created_at))
                        });
                    }
                } catch (error) {
                    console.error('Failed to load essay topics from database:', error);
                }
            };
            
            loadData();
        }
    ]
});

export const useMrMate = (): UseMrMateReturn => {
    const [state, setState] = useRecoilState(mrMateState);
    const resetMrMate = useResetRecoilState(mrMateState);

    // Fetch topics from API
    const fetchTopics = useCallback(async (): Promise<EssayTopic[]> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const topics = await getMrMateTopics();
            
            if (topics !== null) {
                // Ensure we only keep the latest 3 topics
                const recentTopics = topics.slice(0, 3);
                
                // Save to local database cache
                await socialDatabase.saveEssayTopics(recentTopics);
                
                setState(prev => ({
                    ...prev,
                    topics: recentTopics,
                    loading: false,
                    lastUpdated: Math.floor(Date.now() / 1000)
                }));
                
                return recentTopics;
            } else {
                setState(prev => ({ 
                    ...prev, 
                    error: "Failed to fetch Mr. MATE topics", 
                    loading: false 
                }));
                return [];
            }
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: error instanceof Error ? error.message : "Failed to fetch Mr. MATE topics",
                loading: false
            }));
            console.error('Failed to fetch Mr. MATE topics:', error);
            return [];
        }
    }, [setState]);

    // Refresh topics (force fetch even if cached)
    const refreshTopics = useCallback(async (): Promise<EssayTopic[]> => {
        return await fetchTopics();
    }, [fetchTopics]);

    return {
        topics: state.topics,
        loading: state.loading,
        error: state.error,
        lastUpdated: state.lastUpdated,
        
        // Functions
        fetchTopics,
        refreshTopics,
        
        reset: resetMrMate
    };
};